import { currentFactValue, splitFactKey, type EntityState } from './entities-fold.js';
import { type FacetTemplate } from './pack.js';
import { type LocalizedString } from './predicate.js';

/**
 * A facet resolved against ONE entity: the facet template plus the entity's
 * own valued facts (by bare predicate) that enroll in it. Only facets with
 * ≥1 enrolled fact are produced — an empty facet has nothing to reveal.
 */
export interface ResolvedFacet {
  id: string;
  label?: LocalizedString;
  safe_to_bulk_reveal: boolean;
  /** Bare predicate ids on this entity (with a current value) in the facet, sorted. */
  predicates: string[];
}

/**
 * Derive an entity's bulk-reveal facets (`03a §11`, `30 §7.10`). Pure +
 * decoupled from the registry/trait machinery: the caller supplies the facet
 * templates gathered from the entity's traits' `default_facets`, and a
 * `predicateFacetOf` lookup (a predicate's `default_facet`). A facet is the
 * set of the entity's valued facts whose predicate maps to it — derived, not
 * stored (the reveal log stays per-fact; the facet is only a UI shortcut).
 *
 * Repeatable `predicate#instance` keys collapse onto the bare predicate.
 * Facets follow `facetTemplates` order; predicates are sorted + deduped.
 */
export function entityFacets(
  entity: EntityState,
  facetTemplates: readonly FacetTemplate[],
  predicateFacetOf: (predicateId: string) => string | undefined,
): ResolvedFacet[] {
  const members = new Map<string, Set<string>>();
  for (const [factKey, fact] of Object.entries(entity.facts)) {
    if (currentFactValue(fact) === undefined) continue;
    const predicate = splitFactKey(factKey).predicate;
    const facetId = predicateFacetOf(predicate);
    if (!facetId) continue;
    let set = members.get(facetId);
    if (!set) {
      set = new Set();
      members.set(facetId, set);
    }
    set.add(predicate);
  }

  const out: ResolvedFacet[] = [];
  for (const tmpl of facetTemplates) {
    const set = members.get(tmpl.id);
    if (!set || set.size === 0) continue;
    out.push({
      id: tmpl.id,
      ...(tmpl.label !== undefined ? { label: tmpl.label } : {}),
      safe_to_bulk_reveal: tmpl.safe_to_bulk_reveal,
      predicates: [...set].sort(),
    });
  }
  return out;
}
